import { Injectable } from '@angular/core';

import { ObjectCheckerService } from '../../../../services/object-checker.service';
import { EventLogService, Actions } from '../../../../services/eventLog';
import { AwningService } from '../../../../services/awning/awning.service';
import { ProductsService } from '../../../../services/products.service';
import { mountableObjectIsWithinWallArea } from '../../../../services/wall/utils/mountable-object-is-within-wall-area';
import { pipeFns } from '../../../../utils/pipe-fns';
import { StageService } from '../../../../services/stage.service';
import { CollisionService } from '../../../../services/collision/collision.service';
import { Model3DService } from '../../../../services/model3-d.service';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';

declare const THREE: any;

@Injectable()
export class AwningResizeHandler {
  name: string;

  constructor(
    private productsService: ProductsService,
    private stageService: StageService,
    private collisionService: CollisionService,
    protected objectCheckerSrvc: ObjectCheckerService,
    private eventLog: EventLogService,
    private awningService: AwningService,
    private model3D: Model3DService,
    private loadingSpinnerService: LoadingSpinnerService
  ) {
    this.name = 'awningResizeControls';
  }

  private _extensionAllowed(base, awningEntity, staticObjects) {
    const { length: awningLength, awningType, awningConnectingPin } = awningEntity;
    const { width, height } = base.userData.entity;
    const groupSizes = this.awningService.getSizes(base);
    const shift =
      this.awningService.getAwningPositionType(awningType) === 'left'
        ? -awningLength / 2
        : awningLength / 2;
    const newPosition = new THREE.Vector3(shift, 0, 0);
    const length = groupSizes.x + awningLength - awningConnectingPin;

    staticObjects = this.stageService.getChildrenByPredicate(obj =>
      this.awningService.isAwning(obj)
    );
    const filteredStaticObjects = staticObjects.filter(
      so => !this.awningService.isFromTheSameGroup(base, so) && so !== base
    );

    base.localToWorld(newPosition);

    const collided = this.collisionService.isCollide(base, filteredStaticObjects, {
      newPosition,
      newSize: new THREE.Vector3(length, height, width)
    });
    return (
      mountableObjectIsWithinWallArea(
        base.parent,
        { width, length, height },
        newPosition,
        base.rotation
      ) && !collided
    );
  }

  private _getAwningExtensionPosition(base, entity, extension: 'left' | 'right') {
    const { length, awningConnectingPin } = entity;
    const groupSizes = this.awningService.getSizes(base);
    const groupCenter = this.awningService.getCenter(base);
    const { awningPin } = base.userData.entity;
    let shift = groupSizes.x / 2 - awningPin - awningConnectingPin + length / 2;
    if (extension === 'left') shift = -shift;
    const transormPosition = pipeFns(position => position.add(new THREE.Vector3(shift, 0, 0)));
    return base.localToWorld(transormPosition(base.worldToLocal(groupCenter)));
  }

  private async _addAwningExtension(base, entity, position) {
    try {
      this.loadingSpinnerService.spinnerLoadingListAdd('ObjectAddService');
      const obj = await this.model3D.create(entity, {
        position,
        wall: base.parent.name,
        show3DObject: true
      });
      obj.name = entity.productType;
      this.eventLog.dispatch(new Actions.AddObjectAction(obj));
      THREE.SceneUtils.attach(obj, this.stageService.getScene(), base);
      obj.rotation.copy(base.rotation);
      this.stageService.objectAdded.next(obj);
      this.loadingSpinnerService.spinnerLoadingListRemove('ObjectAddService');
    } catch (err) {
      console.log('error');
    }
  }

  async invoke(awining, buttonName: 'left' | 'right') {
    const { awningType: baseAwning } = awining.userData.entity;
    const staticObjects = this.stageService.getAllExceptTheObject(awining);
    try {
      const [awningEntity] = await this.productsService.getExtensionAwning(baseAwning, buttonName);
      if (!this._extensionAllowed(awining, awningEntity, staticObjects)) return false;
      const position = this._getAwningExtensionPosition(awining, awningEntity, buttonName);
      await this._addAwningExtension(awining, awningEntity, position);
      return false;
    } catch (err) {
      console.log('error');
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/3D/ObjectControls/Handlers/AwningResizeHandler.ts